import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  CircularProgress,
  Typography,
} from '@mui/material';
import { X, Download } from 'lucide-react';
import { auditLogsService } from 'components/api/auditLogs';
import { getAuditLogDetailStyles } from './AuditLogDetailModal.styles';

interface AuditLogDetailModalProps {
  open: boolean;
  onClose: () => void;
  logKey: string;
  theme?: 'light' | 'dark';
}

const AuditLogDetailModal: React.FC<AuditLogDetailModalProps> = ({
  open,
  onClose,
  logKey,
  theme = 'dark',
}) => {
  const isDark = theme === 'dark';
  const styles = getAuditLogDetailStyles(isDark);
  const [data, setData] = useState<any>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && logKey) {
      const fetchDetail = async () => {
        setLoading(true);
        setError(null);
        try {
          // Fetch both entry data and download URL in parallel
          const [entryResult, downloadResult] = await Promise.all([
            auditLogsService.getAuditLogEntry(logKey),
            auditLogsService.getDownloadUrl(logKey),
          ]);

          setData(entryResult?.data?.entry ?? {});
          setDownloadUrl(downloadResult?.data?.url ?? null);
        } catch (err: any) {
          setError(err.message || 'Failed to fetch log details');
        } finally {
          setLoading(false);
        }
      };

      fetchDetail();
    } else {
      setData(null);
      setDownloadUrl(null);
    }
  }, [open, logKey]);

  const handleDownload = async () => {
    if (!downloadUrl) return;

    try {
      // Robust download using blob for cross-origin URLs
      const res = await fetch(downloadUrl);
      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', logKey.split('/').pop() || 'log.json');
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: styles.paper,
      }}
    >
      <DialogTitle sx={styles.title}>
        Log Entry Details
        <IconButton onClick={onClose} sx={{ color: isDark ? '#8698a6' : '#5d7182' }}>
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={styles.content}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: '#00b3f5' }} />
          </Box>
        ) : error ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : data ? (
          <Box component="pre" sx={styles.pre}>
            {JSON.stringify(data, null, 2)}
          </Box>
        ) : (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="textSecondary">No data found for this entry.</Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={styles.actions}>
        {downloadUrl && (
          <Button
            onClick={handleDownload}
            sx={styles.downloadBtn}
            startIcon={<Download size={18} />}
          >
            Download JSON
          </Button>
        )}
        <Button onClick={onClose} variant="contained" sx={styles.closeBtn}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AuditLogDetailModal;
